import React, { useState, useMemo } from 'react';
import { useOrdenesCompra } from '../hooks/useOrdenesCompra';
import PageHeader from './PageHeader';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  TrendingUp,
  DollarSign,
  Package,
  Clock,
  Calendar,
  Download,
  RefreshCw,
} from 'lucide-react';

// Colores para los gráficos
const COLORS = {
  borrador: '#f59e0b',
  enviada: '#3b82f6',
  recibida: '#10b981',
  parcial: '#f97316',
  cancelada: '#ef4444',
};

// Función para formatear moneda
const formatearMoneda = (cantidad: number): string => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cantidad);
};

interface MetricaCard {
  titulo: string;
  valor: string | number;
  icono: React.ReactNode;
  color: string;
  descripcion?: string;
  cambio?: {
    valor: number;
    esPositivo: boolean;
  };
}

const ReportesOrdenesCompra: React.FC = () => {
  const [filtroFecha, setFiltroFecha] = useState({
    desde: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0], // Últimos 30 días
    hasta: new Date().toISOString().split('T')[0],
  });

  const { data: ordenesData, isLoading, error, refetch } = useOrdenesCompra();
  const ordenes = ordenesData?.data || [];

  // Filtrar órdenes por fecha
  const ordenesFiltradas = useMemo(() => {
    return ordenes.filter((orden) => {
      const fechaOrden = new Date(orden.fecha);
      const fechaDesde = new Date(filtroFecha.desde);
      const fechaHasta = new Date(filtroFecha.hasta);
      return fechaOrden >= fechaDesde && fechaOrden <= fechaHasta;
    });
  }, [ordenes, filtroFecha]);

  // Calcular métricas principales
  const metricas = useMemo(() => {
    const totalOrdenes = ordenesFiltradas.length;
    const totalMonto = ordenesFiltradas.reduce(
      (sum, orden) => sum + (orden.total || 0),
      0
    );
    const ordenesRecibidas = ordenesFiltradas.filter(
      (o) => o.estado === 'recibida'
    ).length;
    const ordenesEnProceso = ordenesFiltradas.filter((o) =>
      ['enviada', 'parcial'].includes(o.estado)
    ).length;

    const promedioMonto = totalOrdenes > 0 ? totalMonto / totalOrdenes : 0;
    const porcentajeCompletadas =
      totalOrdenes > 0 ? (ordenesRecibidas / totalOrdenes) * 100 : 0;

    return {
      totalOrdenes,
      totalMonto,
      ordenesRecibidas,
      ordenesEnProceso,
      promedioMonto,
      porcentajeCompletadas,
    };
  }, [ordenesFiltradas]);

  // Datos para gráfico de estados
  const datosEstados = useMemo(() => {
    const estados = ordenesFiltradas.reduce(
      (acc, orden) => {
        acc[orden.estado] = (acc[orden.estado] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    return Object.entries(estados).map(([estado, cantidad]) => ({
      estado: estado.charAt(0).toUpperCase() + estado.slice(1),
      cantidad,
      color: COLORS[estado as keyof typeof COLORS] || '#6b7280',
    }));
  }, [ordenesFiltradas]);

  // Datos para gráfico temporal
  const datosTemporales = useMemo(() => {
    const datosAgrupados = ordenesFiltradas.reduce(
      (acc, orden) => {
        const fecha = new Date(orden.fecha).toISOString().split('T')[0];
        if (!acc[fecha]) {
          acc[fecha] = { fecha, cantidad: 0, monto: 0 };
        }
        acc[fecha].cantidad += 1;
        acc[fecha].monto += orden.total || 0;
        return acc;
      },
      {} as Record<string, { fecha: string; cantidad: number; monto: number }>
    );

    return Object.values(datosAgrupados).sort((a, b) =>
      a.fecha.localeCompare(b.fecha)
    );
  }, [ordenesFiltradas]);

  // Datos para proveedores
  const datosProveedores = useMemo(() => {
    const proveedores = ordenesFiltradas.reduce(
      (acc, orden) => {
        const nombreProveedor =
          typeof orden.proveedor === 'string'
            ? orden.proveedor
            : orden.proveedor?.nombre || 'Sin proveedor';
        if (!acc[nombreProveedor]) {
          acc[nombreProveedor] = { cantidad: 0, monto: 0 };
        }
        acc[nombreProveedor].cantidad += 1;
        acc[nombreProveedor].monto += orden.total || 0;
        return acc;
      },
      {} as Record<string, { cantidad: number; monto: number }>
    );

    return Object.entries(proveedores)
      .map(([proveedor, datos]) => ({
        proveedor,
        cantidad: datos.cantidad,
        monto: datos.monto,
      }))
      .sort((a, b) => b.monto - a.monto)
      .slice(0, 10); // Top 10 proveedores
  }, [ordenesFiltradas]);

  const tarjetasMetricas: MetricaCard[] = [
    {
      titulo: 'Total Órdenes',
      valor: metricas.totalOrdenes,
      icono: <Package className="h-6 w-6" />,
      color: 'text-blue-600',
      descripcion: 'Órdenes en el período',
    },
    {
      titulo: 'Monto Total',
      valor: formatearMoneda(metricas.totalMonto),
      icono: <DollarSign className="h-6 w-6" />,
      color: 'text-green-600',
      descripcion: 'Valor total de órdenes',
    },
    {
      titulo: 'Órdenes Completadas',
      valor: `${metricas.ordenesRecibidas} (${metricas.porcentajeCompletadas.toFixed(1)}%)`,
      icono: <TrendingUp className="h-6 w-6" />,
      color: 'text-emerald-600',
      descripcion: 'Recibidas exitosamente',
    },
    {
      titulo: 'En Proceso',
      valor: metricas.ordenesEnProceso,
      icono: <Clock className="h-6 w-6" />,
      color: 'text-orange-600',
      descripcion: 'Enviadas o parciales',
    },
  ];

  const exportarReporte = () => {
    const csvContent = [
      ['Fecha', 'Número Orden', 'Proveedor', 'Estado', 'Total'],
      ...ordenesFiltradas.map((orden) => [
        new Date(orden.fecha).toLocaleDateString('es-CL'),
        orden.numero,
        typeof orden.proveedor === 'string'
          ? orden.proveedor
          : orden.proveedor?.nombre || 'Sin proveedor',
        orden.estado,
        orden.total?.toString() || '0',
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `reporte-ordenes-compra-${filtroFecha.desde}-${filtroFecha.hasta}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Reportes de Órdenes de Compra"
          subtitle="Análisis y estadísticas de órdenes de compra"
        />
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando reportes...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Reportes de Órdenes de Compra"
          subtitle="Análisis y estadísticas de órdenes de compra"
        />
        <div className="flex items-center justify-center h-64">
          <div className="text-red-500">Error al cargar reportes</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 relative">
      <div className="absolute inset-0 bg-white/85 dark:bg-gray-900/85"></div>

      <div className="relative z-10 p-6">
        <div className="space-y-6">
          <PageHeader
            title="Reportes de Órdenes de Compra"
            subtitle="Análisis y estadísticas de órdenes de compra"
          />

          {/* Filtros y controles */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Período:
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={filtroFecha.desde}
                    onChange={(e) =>
                      setFiltroFecha((prev) => ({
                        ...prev,
                        desde: e.target.value,
                      }))
                    }
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                  <span className="text-gray-400">hasta</span>
                  <input
                    type="date"
                    value={filtroFecha.hasta}
                    onChange={(e) =>
                      setFiltroFecha((prev) => ({
                        ...prev,
                        hasta: e.target.value,
                      }))
                    }
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => refetch()}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Actualizar
                </button>
                <button
                  onClick={exportarReporte}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Exportar CSV
                </button>
              </div>
            </div>
          </div>

          {/* Tarjetas de métricas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tarjetasMetricas.map((metrica, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {metrica.titulo}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                      {metrica.valor}
                    </p>
                    {metrica.descripcion && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {metrica.descripcion}
                      </p>
                    )}
                  </div>
                  <div className={`${metrica.color}`}>{metrica.icono}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Gráfico de estados */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Distribución por Estado
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={datosEstados}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ estado, cantidad }: any) =>
                      `${estado}: ${cantidad}`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="cantidad"
                  >
                    {datosEstados.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Gráfico temporal */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Tendencia Temporal
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={datosTemporales}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="fecha"
                    tickFormatter={(value: any) =>
                      new Date(value).toLocaleDateString('es-CL', {
                        month: 'short',
                        day: 'numeric',
                      })
                    }
                  />
                  <YAxis />
                  <Tooltip
                    labelFormatter={(value: any) =>
                      new Date(value).toLocaleDateString('es-CL')
                    }
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="cantidad"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    name="Cantidad de Órdenes"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top proveedores */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Top 10 Proveedores por Monto
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={datosProveedores} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={formatearMoneda} />
                <YAxis
                  dataKey="proveedor"
                  type="category"
                  width={150}
                  tickFormatter={(value: any) =>
                    value.length > 20 ? `${value.substring(0, 20)}...` : value
                  }
                />
                <Tooltip
                  formatter={(value: any) => formatearMoneda(Number(value))}
                />
                <Bar dataKey="monto" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tabla resumen */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Resumen Detallado por Proveedor
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Proveedor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Cantidad Órdenes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Monto Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Promedio por Orden
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {datosProveedores.map((proveedor, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {proveedor.proveedor}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {proveedor.cantidad}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {formatearMoneda(proveedor.monto)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {formatearMoneda(proveedor.monto / proveedor.cantidad)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportesOrdenesCompra;
